import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Panel,
  PanelFooter,
  PanelHeader,
  SegmentedControl,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { AttachmentViewer, type AttachmentViewerSubject } from '@/components/attachments/AttachmentViewer';
import { api, ApiError } from '@/lib/api';
import {
  ATTACHMENT_ACCEPT,
  attachmentContentType,
  attachmentSubtitle,
  formatAttachmentSize,
  isAllowedAttachmentType,
  MAX_ATTACHMENT_BYTES,
  oversizeRefusal,
  typeRefusal,
  uploaderLabel,
} from '@/lib/company-attachments';
import { saveBlob } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import type { CompanyAttachment, CompanyAttachmentKind } from '@/lib/types';

/*
 * The files and links kept against a company, on its own record.
 *
 * ## 🔴 THE GATE IS BORROWED FROM `companies`, DELIBERATELY
 *
 * Reading is open to anybody who can read the company, so **the panel and the
 * list are ALWAYS rendered** — there is no `canRead` early return here, unlike
 * `CompanyEventInterestPanel` beside it, which gates on a module of its own.
 * What gates is the CONTROLS: attach and edit on `ability.canUpdate('companies')`,
 * delete on `ability.isAdmin`.
 *
 * ⚠️ `{canUpdate && <Panel/>}` would hide the VALUES from a reader who is
 * entitled to see them — the mistake this app already records against gating a
 * READ on a WRITE, where a staff directory fetched only for writers left every
 * other reader an empty picker indistinguishable from a failed load.
 *
 * **The borrow is deliberate and is not an oversight**: an attachment IS company
 * data — the same record, the same readers, the same people entitled to change
 * it — so `companies` is the grant that already answers "may this person edit
 * this account". ⛔ **The day attachments need a narrower grant than the company
 * itself is the day this becomes its own module**, with its own slug, its own
 * migration and its own column in the permission grid. Until somebody asks for
 * that, a second slug would be a question nobody has put to the server, and an
 * invented slug resolves to nothing — which degrades OPEN, showing the controls
 * to exactly the people a narrower grant was meant to keep out.
 *
 * ⚠️ The gate is the OPTIMISATION and the server's refusal is the GUARANTEE. A
 * grant can be taken away under a live access token, which lasts days, so every
 * write below handles its own failure rather than assuming a hidden control was
 * enough.
 *
 * ## The upload is THREE steps and only two of them are our API
 *
 * See {@link AttachDialog}. The middle step is a bare PUT straight to object
 * storage, it is the slow one, and any failure AFTER it means the bytes are
 * already stored. Reporting all three under one spinner would make that read as
 * a failed upload — so each step is named, and the stored-but-unrecorded state
 * is carried out of the dialog rather than lost with it.
 *
 * ## `embedded`
 *
 * By default (`embedded` absent or `false`) this renders its own `Panel` with a
 * `PanelHeader` carrying the Attach control, exactly as it always has — every
 * existing call site is unchanged. With `embedded: true` it renders NEITHER: the
 * body is returned directly, for a host (a collapsible section on the company
 * page) that has already drawn the border and the heading, and the Attach
 * control moves into a right-aligned toolbar row at the top of the body. It is
 * gated identically in both modes, and withheld (not disabled) for a reader who
 * may not write, so the row renders nothing for them. Every state, sentence,
 * gate and callback is the same in both modes; only the outer chrome goes.
 * `HEADING_ID` is rendered only with the chrome, so nothing points at it when
 * embedded.
 */

const HEADING_ID = 'company-attachments-heading';

/**
 * The three states this surface must always have, as one union so that no
 * render can be in two of them at once.
 *
 * ⚠️ `error` and `ready` are SEPARATE ARMS rather than a message sitting above a
 * row list, because zero attachments is the ordinary answer here: a failed read
 * that still drew the empty state would tell a reader this account has nothing
 * attached, which is a claim rather than a gap.
 */
type ListState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; list: readonly CompanyAttachment[] };

/**
 * Bytes that reached object storage with no record pointing at them.
 *
 * 🔴 **THIS IS THE WHOLE REASON THE THREE STEPS ARE REPORTED SEPARATELY.** It is
 * reachable only between step 2 and step 3, and from the reader's side it is
 * invisible: nothing appears in the list, and the natural reading of "it failed"
 * is that the file never left the machine. Retrying from here re-runs step 3
 * ALONE — the bytes are already up, so re-uploading them would spend the
 * transfer a second time and leave a second orphan behind.
 */
interface UnrecordedUpload {
  storageKey: string;
  fileName: string;
  title: string;
  note: string;
}

/**
 * The sentence to show for a failure, preferring the one it carried.
 *
 * ⚠️ `uploadToSignedUrl` throws a plain `Error` naming the HTTP status rather
 * than an `ApiError`, so a caller cannot assume every failure on this surface is
 * one of ours — and the store's own body is deliberately not surfaced, which is
 * why the fallback has to be a sentence of our own rather than a blank.
 */
function messageOf(caught: unknown, fallback: string): string {
  return caught instanceof Error && caught.message !== '' ? caught.message : fallback;
}

function PaperclipIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M21.44 11.05 12.25 20.24a5.5 5.5 0 0 1-7.78-7.78l8.49-8.49a3.5 3.5 0 1 1 4.95 4.95l-8.49 8.49a1.5 1.5 0 0 1-2.12-2.12l7.78-7.78" />
    </svg>
  );
}

/**
 * The loading placeholder, in the real shape rather than a spinner — three rows,
 * each a title line over a meta line. Three because a company that has anything
 * attached usually has a handful: one would read as a record holding a single
 * file, and a stack of ten would promise a history that is not there.
 */
function AttachmentsSkeleton() {
  return (
    <div>
      {Array.from({ length: 3 }, (_, row) => (
        <div key={row} className="border-b border-border px-4 py-3 last:border-b-0">
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-12" />
          </div>
          <Skeleton className="mt-2 h-3 w-64" />
        </div>
      ))}
    </div>
  );
}

/**
 * The consequence sentence for one deletion, worded from its `kind`.
 *
 * ⚠️ Split out so the two arms sit side by side and can be read against each
 * other: they describe genuinely different destructions, and only the file arm
 * may claim that stored bytes are going. ⛔ **Neither promises an undo**, because
 * there is none — no endpoint in this API restores either a row or an object.
 */
function deleteDescription(attachment: CompanyAttachment): ReactNode {
  return attachment.kind === 'file' ? (
    <>
      <strong className="font-semibold text-fg">{attachment.title}</strong> will be removed from
      this company, and the stored file is deleted with it. Neither can be recovered.
    </>
  ) : (
    <>
      <strong className="font-semibold text-fg">{attachment.title}</strong> will be removed from
      this company. The link itself is not affected, and the attachment cannot be recovered.
    </>
  );
}

/**
 * One attachment.
 *
 * 🔴 **THE ACTION IS CHOSEN BY `kind`, NEVER BY WHICH FIELDS ARE POPULATED.** A
 * file row offers Download — through the API, by id — and a link row offers
 * Open, which is a real anchor into a new tab. ⛔ **A link is never downloaded
 * through the API and a file is never opened in a tab**: the two are different
 * resources that happen to share a row shape, and branching on `url !== null`
 * would make the affordance depend on a column rather than on what the thing IS.
 * `fileName`, `contentType`, `sizeBytes` and `url` are ALL nullable on both
 * arms, so every one of them is a bad discriminant.
 */
function AttachmentRow({
  companyId,
  attachment,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
}: {
  companyId: number;
  attachment: CompanyAttachment;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (attachment: CompanyAttachment) => void;
  onDelete: (attachment: CompanyAttachment) => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Built once on click and held here, so the viewer's load effect sees a stable subject. */
  const [viewing, setViewing] = useState<AttachmentViewerSubject | null>(null);

  /*
   * The in-flight controller, so an unmount aborts the request rather than
   * leaving it to resolve into a component that is gone — and, more to the
   * point, rather than writing a file to disk on behalf of a page the reader has
   * already navigated away from. Copied from `DownloadPdfButton`, which owns the
   * same problem for a document.
   */
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const download = useCallback(async () => {
    /*
     * Checked as well as expressed through `loading`: the disabled button is the
     * optimisation, this is the guarantee. A double-fire writes the same bytes
     * to disk twice and spends the transfer twice.
     */
    if (downloading) return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    setDownloading(true);
    setError(null);

    try {
      /*
       * ⚠️ NOT an envelope — `downloadCompanyAttachment` goes through
       * `requestBinary` and answers the bytes directly, unlike the four JSON
       * methods on this resource, which all carry one.
       */
      const { filename, blob } = await api.downloadCompanyAttachment(
        companyId,
        attachment.id,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      /*
       * The server's own name, never `attachment.title`: the title is what
       * somebody called this on screen and the file name is what the bytes were
       * stored as. Saving under the title would rename a customer's document to
       * a label a colleague typed into a text box.
       */
      saveBlob(filename, blob);
    } catch (caught) {
      if (controller.signal.aborted) return;
      setError(messageOf(caught, 'The file couldn’t be downloaded.'));
    } finally {
      /*
       * Not guarded on `aborted`: an aborted request means this row is being
       * torn down or superseded, and in the supersede case the state must still
       * settle or the button spins for ever with nothing to retry. Setting state
       * on an unmounted component is a no-op in React 19.
       */
      setDownloading(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [attachment.id, companyId, downloading]);

  return (
    <article className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="m-0 min-w-0 text-sm font-semibold text-fg [overflow-wrap:anywhere]">
          {attachment.title}
        </h3>
        {/*
          The kind, said in a word. Not decoration: it is what decides which
          action the row offers, so a reader meeting Open on one row and Download
          on the next can tell why. Neutral tone on both — neither is a status
          and neither is something to act on.
        */}
        <Badge tone="neutral" variant="outline">
          {attachment.kind === 'file' ? 'File' : 'Link'}
        </Badge>
      </div>

      <p className="m-0 mt-1 text-xs text-fg-secondary [overflow-wrap:anywhere]">
        {attachmentSubtitle(attachment)}
      </p>

      {/*
        Who added it and when. `uploaderLabel` owns the three-way that
        `uploadedBy` can be — a person, a person whose account has since been
        deleted, and nobody at all — because deletion is the only way a user
        leaves this system, so a `null` here is an ordinary and growing
        population rather than an edge.
      */}
      <p className="m-0 mt-0.5 text-2xs text-fg-muted [overflow-wrap:anywhere]">
        {uploaderLabel(attachment.uploadedBy)} · {formatDateTime(attachment.createdAt)}
      </p>

      {/*
        The note, when there is one. Absent rather than a dash when there is not:
        a dash in a prose slot reads like a value somebody entered.
      */}
      {attachment.note !== null && attachment.note !== '' && (
        <p className="m-0 mt-2 text-xs whitespace-pre-line text-fg-secondary [overflow-wrap:anywhere]">
          {attachment.note}
        </p>
      )}

      {/*
        ⚠️ `flex-wrap`, because at 320px four small buttons do not share a row —
        and a row of controls that cannot wrap is how a control ends up off the
        viewport with nothing reporting it.
      */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {attachment.kind === 'file' ? (
          <>
            {/* Opens in the in-app viewer (owner's decision, 2026-09-25). */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setViewing({
                  kind: 'file',
                  title: attachment.title,
                  fileName: attachment.fileName,
                  contentType: attachment.contentType,
                  load: (signal) => api.downloadCompanyAttachment(companyId, attachment.id, signal),
                })
              }
            >
              Open
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={downloading}
              onClick={() => void download()}
            >
              Download
            </Button>
          </>
        ) : attachment.url !== null && attachment.url !== '' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setViewing({ kind: 'link', title: attachment.title, url: attachment.url ?? '' })}
          >
            Open
          </Button>
        ) : null}
        {attachment.kind === 'link' && (attachment.url === null || attachment.url === '') && (
          /*
           * ⛔ A link row with no address. The row still renders — it is a real
           * record somebody created, and hiding it would make a defective row
           * indistinguishable from one that was never added — but there is
           * nothing to open, so NO control is offered rather than a dead one.
           *
           * ⚠️ The sentence is a FACT ABOUT THE DATA the reader cannot otherwise
           * see, which is one of the kinds the no-helper-text rule keeps: without
           * it this row simply has fewer buttons than its neighbour and nothing
           * says why. It explains no control, because there is none.
           */
          <p className="m-0 text-xs text-fg-muted">This link has no address recorded.</p>
        )}

        {canUpdate && (
          <Button variant="ghost" size="sm" onClick={() => onEdit(attachment)}>
            Edit
          </Button>
        )}
        {canDelete && (
          <Button variant="ghost" size="sm" onClick={() => onDelete(attachment)}>
            Delete
          </Button>
        )}
      </div>

      {/*
        A refusal, and nothing else. There is deliberately no "Saved to your
        downloads" line: the browser owns what happens after the click, so a
        download blocked by an extension looks identical from here and a success
        message would be a claim this component cannot support.
      */}
      {error !== null && (
        <p role="alert" className="m-0 mt-2 text-xs text-danger [overflow-wrap:anywhere]">
          {error}
        </p>
      )}

      <AttachmentViewer subject={viewing} onClose={() => setViewing(null)} />
    </article>
  );
}

/**
 * Which of the three requests is in flight.
 *
 * 🔴 **NAMED SEPARATELY BECAUSE THE BYTES CHANGE HANDS BETWEEN THEM.** A single
 * "Attaching…" covering all three would make a failure at `recording` read as a
 * failed upload — and at that point the file is already in object storage, so
 * the reader's natural next move, choosing it and uploading it again, leaves a
 * second orphan behind. `uploading` is the slow one and the only one whose
 * duration a reader will notice.
 */
type AttachPhase = 'idle' | 'preparing' | 'uploading' | 'recording';

const PHASE_LABEL: Record<Exclude<AttachPhase, 'idle'>, string> = {
  preparing: 'Preparing the upload…',
  uploading: 'Uploading the file…',
  recording: 'Recording the attachment…',
};

/**
 * This form's own write keys, for partitioning a 422.
 *
 * ⚠️ A refusal naming a key this form does not render must NOT be marked on a
 * control: it would be highlighted nowhere while still inflating the count of
 * fields needing attention, which is the "one field needs attention, highlighted
 * below" with nothing highlighted that `routeFieldErrors` exists to prevent.
 */
const ATTACH_WRITE_KEYS = ['title', 'note', 'url', 'fileName', 'storageKey'] as const;

function AttachDialog({
  companyId,
  onClose,
  onAttached,
  onLeftUnrecorded,
}: {
  companyId: number;
  onClose: () => void;
  onAttached: (list: readonly CompanyAttachment[]) => void;
  /**
   * 🔴 **CALLED WHEN THE DIALOG CLOSES OVER BYTES THAT REACHED STORAGE AND WERE
   * NEVER RECORDED.** Without it the fact dies with the dialog: nothing appears
   * in the list, and a reader who dismissed a failure has no way left to learn
   * that the transfer itself succeeded. The panel states it and offers the one
   * retry that does not upload the file a second time.
   */
  onLeftUnrecorded: (pending: UnrecordedUpload) => void;
}) {
  const [kind, setKind] = useState<CompanyAttachmentKind>('file');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');

  const [phase, setPhase] = useState<AttachPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Set once step 2 lands; cleared only when step 3 lands. See {@link UnrecordedUpload}. */
  const [stored, setStored] = useState<{ storageKey: string; fileName: string } | null>(null);

  /*
   * Whether the title belongs to the reader or to the seeder.
   *
   * ⚠️ **Set even when they EMPTY the box**, which is the half that is easy to
   * miss: without it, clearing a seeded title hands control back to the seeder
   * and choosing another file re-fills it, so the field fights the person typing
   * in it.
   */
  const titleTouched = useRef(false);

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const busy = phase !== 'idle';

  /*
   * ⚠️ **The handoff happens HERE rather than in an unmount cleanup.** This runs
   * on the paths a reader can actually take out of the dialog — the ×, Escape
   * and Cancel — where a cleanup would also fire on the successful ones, after
   * `stored` had already been cleared, and would depend on the order two state
   * writes happened to land in.
   */
  const close = useCallback(() => {
    if (stored !== null) onLeftUnrecorded({ ...stored, title, note });
    onClose();
  }, [note, onClose, onLeftUnrecorded, stored, title]);

  const chooseFile = useCallback((chosen: File | null) => {
    setError(null);

    if (chosen === null) {
      setFile(null);
      return;
    }

    /*
     * Both checks run BEFORE anything is requested, so nobody pays for a
     * transfer the server is going to refuse — and both refusals name the file,
     * because a dialog that holds one file at a time still gets used twice in a
     * row, and "that file is too large" answers the wrong question once two
     * files have been tried.
     *
     * ⚠️ **The server re-checks both.** This is a courtesy, not the rule: a
     * client-side limit is a convention, and the day the two disagree the
     * server's answer is the one that decides.
     */
    if (chosen.size > MAX_ATTACHMENT_BYTES) {
      setFile(null);
      setError(oversizeRefusal(chosen.name, chosen.size));
      return;
    }
    /*
     * ⚠️ **`file.type` raw, parameters and all.** `isAllowedAttachmentType`
     * strips a `; charset=…` itself, so normalising here would be a second copy
     * of a rule that module owns — and the browser emits a bare type in practice
     * anyway.
     *
     * ⚠️ **An EMPTY `file.type` is refused, and that is the intended direction.**
     * A browser that cannot identify a file reports `''`, which is on no allow
     * list, so this declines before spending the transfer; the server is the
     * authority and `typeRefusal` names the way round it — attach it as a link.
     * ⛔ Never soften this into a pass: an unknown type resolved as allowed is a
     * client widening a rule it does not own.
     */
    if (!isAllowedAttachmentType(attachmentContentType(chosen))) {
      setFile(null);
      setError(typeRefusal(chosen.name));
      return;
    }

    setFile(chosen);
    if (!titleTouched.current) setTitle(chosen.name);
  }, []);

  const failed = useCallback((caught: unknown, fallback: string) => {
    if (caught instanceof ApiError) {
      const { fields, unroutable } = caught.routeFieldErrors([...ATTACH_WRITE_KEYS]);
      setFieldErrors(fields);
      setError(
        unroutable.length > 0 ? unroutable.join(' ') : (caught.formMessage ?? caught.message),
      );
      return;
    }
    setError(messageOf(caught, fallback));
  }, []);

  /**
   * Step 3, on its own — reached both by the ordinary flow and by the retry
   * after a failure at step 3, which must NOT upload the bytes again.
   */
  const record = useCallback(
    async (storageKey: string, fileName: string, controller: AbortController) => {
      setPhase('recording');
      const answer = await api.createCompanyAttachment(
        companyId,
        { kind: 'file', storageKey, fileName, title, ...(note === '' ? {} : { note }) },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setStored(null);
      onAttached(answer.data);
      onClose();
    },
    [companyId, note, onAttached, onClose, title],
  );

  const submit = useCallback(async () => {
    if (busy) return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    setError(null);
    setFieldErrors({});

    try {
      if (kind === 'link') {
        setPhase('recording');
        const answer = await api.createCompanyAttachment(
          companyId,
          { kind: 'link', url, title, ...(note === '' ? {} : { note }) },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        onAttached(answer.data);
        onClose();
        return;
      }

      /*
       * The retry after a failure at step 3: the bytes are up, so the record is
       * the only thing missing and re-uploading would be a second transfer and a
       * second orphan.
       */
      if (stored !== null) {
        await record(stored.storageKey, stored.fileName, controller);
        return;
      }

      if (file === null) return;

      /*
       * 🔴 **THE CONTENT TYPE IS CAPTURED ONCE AND THE SAME VALUE IS USED
       * TWICE.** The server pins `content-type` into the signature, so the
       * string handed to `uploadToSignedUrl` must be byte-for-byte the string
       * the target was signed for. Reading `file.type` a second time at the
       * point of upload is the tempting version and it is not safe: a browser
       * that appends `; charset=utf-8` produces a **403 from object storage that
       * reads as a broken link rather than as a type mismatch**.
       *
       * ⚠️ `attachmentContentType` falls back to the EXTENSION when the browser
       * reports nothing (common for `.msg`, `.eml`, `.heic`), and to `''` when
       * the extension is unknown too — never to `application/octet-stream`,
       * which would put a claim about the bytes into a signature.
       */
      const contentType = attachmentContentType(file);

      setPhase('preparing');
      const target = await api.createCompanyAttachmentUploadTarget(
        companyId,
        { fileName: file.name, contentType, sizeBytes: file.size },
        controller.signal,
      );
      if (controller.signal.aborted) return;

      setPhase('uploading');
      await api.uploadToSignedUrl(target.data.uploadUrl, file, contentType, controller.signal);
      if (controller.signal.aborted) return;

      /*
       * ⚠️ **From this line on, the bytes exist whatever happens next.** The key
       * is held in state so a failure at step 3 can be retried without a second
       * transfer, and so closing the dialog hands the fact to the panel instead
       * of losing it.
       */
      setStored({ storageKey: target.data.storageKey, fileName: file.name });
      await record(target.data.storageKey, file.name, controller);
    } catch (caught) {
      if (controller.signal.aborted) return;
      failed(caught, 'The attachment couldn’t be added.');
    } finally {
      setPhase('idle');
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [busy, companyId, failed, file, kind, note, onAttached, onClose, record, stored, title, url]);

  /*
   * ⚠️ **Disabled only when there is literally nothing to attach** — no file
   * chosen, or a blank address — which is a state the reader can see in the
   * empty control right beside the button, so the disabled reason is nameable
   * and on screen. Everything else goes to the server: a client-side refusal on
   * a title or on a URL's shape would be a convention dressed as a rule, and
   * this app's habit on an unprovable refusal is to ENABLE.
   */
  const nothingToAttach = stored === null && (kind === 'file' ? file === null : url.trim() === '');

  return (
    <Modal
      open
      onClose={close}
      title="Attach a file or link"
      size="md"
      /*
       * Losing a half-filled form — or worse, dismissing over bytes already in
       * storage — must not be one clipped click away. Escape still closes, and
       * still hands the fact up.
       */
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={busy}
            disabled={nothingToAttach}
          >
            {stored === null ? 'Attach' : 'Try recording it again'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <SegmentedControl
          label="What is being attached"
          value={kind}
          options={[
            { value: 'file', label: 'File' },
            { value: 'link', label: 'Link' },
          ]}
          onChange={(next) => {
            setKind(next);
            setError(null);
            setFieldErrors({});
          }}
          /*
           * ⛔ Held down once bytes are in storage: switching to a link would
           * strand the upload whose key this dialog is still holding, and the
           * retry above it would have nothing to act on.
           */
          disabled={busy || stored !== null}
        />

        {kind === 'file' ? (
          <Field label="File" required>
            {({ id, 'aria-describedby': describedBy }) => (
              /*
               * ⚠️ **A NATIVE `<input type="file">`, AND THAT IS A DEVIATION FROM
               * THE HOUSE RULE, STATED RATHER THAN LEFT TO BE RE-DERIVED.** The
               * rule is to build from the primitives, and to fix the primitive
               * when one cannot express something — and `Input` is a text
               * control whose fixed height and padding do not survive a file
               * picker's own button. There is no file primitive today; adding
               * one is a change to the shared design system that nobody has
               * asked for, so the shape is written out here from tokens only and
               * this comment is the record that it was a decision.
               */
              <input
                id={id}
                aria-describedby={describedBy}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                disabled={busy || stored !== null}
                onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
                className="w-full cursor-pointer rounded-md border border-border bg-surface px-2.5 py-2 text-sm text-fg file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-surface-sunken file:px-2 file:py-1 file:text-xs file:text-fg-secondary disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-subtle"
              />
            )}
          </Field>
        ) : (
          <Field label="Address" required error={fieldErrors.url}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="url"
                value={url}
                invalid={fieldErrors.url !== undefined}
                disabled={busy}
                onChange={(event) => setUrl(event.target.value)}
              />
            )}
          </Field>
        )}

        {/*
          The chosen file, read back. A FACT the reader cannot otherwise see —
          the native control truncates a long name and says nothing at all about
          size — and the only place the size is legible before the transfer
          starts.
        */}
        {kind === 'file' && file !== null && (
          <p className="m-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
            {file.name} · {formatAttachmentSize(file.size)}
          </p>
        )}

        <Field label="Title" required error={fieldErrors.title}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={title}
              invalid={fieldErrors.title !== undefined}
              disabled={busy}
              onChange={(event) => {
                titleTouched.current = true;
                setTitle(event.target.value);
              }}
            />
          )}
        </Field>

        <Field label="Note" error={fieldErrors.note}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={note}
              invalid={fieldErrors.note !== undefined}
              disabled={busy}
              autoGrow={{ minRows: 2, maxRows: 6 }}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </Field>

        {/*
          Which step is running, named. See {@link AttachPhase}: one label for
          all three would make the slow middle step and the quick last one
          indistinguishable, and a failure after the middle one unreportable.
        */}
        {phase !== 'idle' && (
          <p role="status" className="m-0 text-xs text-fg-secondary">
            {PHASE_LABEL[phase]}
          </p>
        )}

        {/*
          🔴 **THE STORED-BUT-UNRECORDED SENTENCE.** It is the one thing on this
          dialog a reader could not work out for themselves, and getting it wrong
          costs a duplicate upload: the transfer succeeded and only the record is
          missing, so the retry is cheap and choosing the file again is not.
        */}
        {stored !== null && (
          <p className="m-0 text-xs text-warning [overflow-wrap:anywhere]">
            {stored.fileName} finished uploading and was not recorded, so it is not on the company
            yet. Trying again re-records it without uploading it a second time.
          </p>
        )}

        {error !== null && (
          <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function EditDialog({
  companyId,
  attachment,
  onClose,
  onSaved,
}: {
  companyId: number;
  attachment: CompanyAttachment;
  onClose: () => void;
  onSaved: (list: readonly CompanyAttachment[]) => void;
}) {
  const [title, setTitle] = useState(attachment.title);
  const [note, setNote] = useState(attachment.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  /** The note as it would be STORED — `null` when the box is empty. */
  const nextNote = note === '' ? null : note;

  /*
   * ⚠️ **Save is held down when nothing has changed**, and the reason is
   * nameable and is on screen: an empty PATCH is a 422 across this API, so the
   * only reachable outcome of pressing it is a refusal about a request the
   * reader did not knowingly make.
   */
  const unchanged = title === attachment.title && nextNote === attachment.note;

  const save = useCallback(async () => {
    if (saving) return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    setSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      /*
       * ⚠️ **Only what changed.** An emptied note sends an explicit `null`,
       * which is what clears it; an untouched field is omitted entirely, because
       * across this API an omitted key is untouched and spreading the whole
       * record back would rewrite columns nobody opened.
       */
      const patch: { title?: string; note?: string | null } = {};
      if (title !== attachment.title) patch.title = title;
      if (nextNote !== attachment.note) patch.note = nextNote;

      const answer = await api.updateCompanyAttachment(
        companyId,
        attachment.id,
        patch,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      onSaved(answer.data);
      onClose();
    } catch (caught) {
      if (controller.signal.aborted) return;
      if (caught instanceof ApiError) {
        const { fields, unroutable } = caught.routeFieldErrors(['title', 'note']);
        setFieldErrors(fields);
        setError(
          unroutable.length > 0 ? unroutable.join(' ') : (caught.formMessage ?? caught.message),
        );
      } else {
        setError(messageOf(caught, 'The attachment couldn’t be saved.'));
      }
    } finally {
      setSaving(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [
    attachment.id,
    attachment.note,
    attachment.title,
    companyId,
    nextNote,
    onClose,
    onSaved,
    saving,
    title,
  ]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit attachment"
      description={attachment.title}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void save()}
            loading={saving}
            disabled={unchanged}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {/*
          ⚠️ **Only the title and the note.** The update body carries those two
          and nothing else — the file, its name, its type and a link's address
          are not re-editable, because they describe bytes already stored under a
          key. Replacing what an attachment IS is a new attachment and a
          deletion, not an edit.
        */}
        <Field label="Title" required error={fieldErrors.title}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={title}
              invalid={fieldErrors.title !== undefined}
              disabled={saving}
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>

        <Field label="Note" error={fieldErrors.note}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={note}
              invalid={fieldErrors.note !== undefined}
              disabled={saving}
              autoGrow={{ minRows: 2, maxRows: 6 }}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </Field>

        {error !== null && (
          <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

export function CompanyAttachmentsPanel({
  companyId,
  embedded = false,
}: {
  companyId: number;
  /** See the header: `true` drops the outer `Panel` and `PanelHeader`. Default `false`. */
  embedded?: boolean;
}) {
  const { ability } = useAuth();
  /* See the header: the `companies` grant is borrowed, deliberately. */
  const canUpdate = ability.canUpdate('companies');
  // 2026-10-01: deletes follow the set, on the same borrowed `companies` grant.
  const canDelete = ability.canDelete('companies');

  const [nonce, setNonce] = useState(0);
  const [result, setResult] = useState<{ key: string; state: ListState } | null>(null);

  const [attachOpen, setAttachOpen] = useState(false);
  const [editing, setEditing] = useState<CompanyAttachment | null>(null);
  const [deleting, setDeleting] = useState<CompanyAttachment | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [unrecorded, setUnrecorded] = useState<UnrecordedUpload | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [recoverError, setRecoverError] = useState<string | null>(null);

  /*
   * One controller per write, so an unmount aborts whichever is in flight. They
   * are separate refs rather than one shared one: a delete and a recovery can
   * legitimately be started from two different controls, and a shared ref would
   * have each cancel the other.
   */
  const deleteAbortRef = useRef<AbortController | null>(null);
  const recoverAbortRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      deleteAbortRef.current?.abort();
      recoverAbortRef.current?.abort();
    },
    [],
  );

  const key = `${companyId}:${nonce}`;

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  /**
   * ⚠️ **The create and update responses carry the WHOLE list, so this renders
   * what came back rather than re-reading.** A refetch after a write is a second
   * request whose answer can differ from the one the server has just given,
   * which is how a reader watches their own change appear and then vanish.
   */
  const replaceList = useCallback(
    (list: readonly CompanyAttachment[]) => {
      setResult({ key, state: { status: 'ready', list } });
    },
    [key],
  );

  useEffect(() => {
    const controller = new AbortController();

    api
      .listCompanyAttachments(companyId, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        setResult({ key, state: { status: 'ready', list: answer.data } });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          key,
          state: {
            status: 'error',
            message: messageOf(caught, 'The attachments couldn’t be loaded.'),
          },
        });
      });
    /*
     * ⚠️ NO `.finally`, and its absence is a decision rather than an omission.
     * The house rule names `.finally` because of the `setLoading(false)` that
     * normally lives there; loading is DERIVED from the key below, so a settled
     * flag would be redundant with the two writes above — and an empty guarded
     * `.finally` would be a guard that can never fire, which reads to the next
     * person as a live rule. The rule's actual purpose, never write state after
     * an abort, is honoured at both sites that write.
     */
    return () => controller.abort();
  }, [companyId, key]);

  const confirmDelete = useCallback(async () => {
    if (deleting === null || deletePending) return;

    const controller = new AbortController();
    deleteAbortRef.current?.abort();
    deleteAbortRef.current = controller;

    setDeletePending(true);
    setDeleteError(null);

    try {
      await api.deleteCompanyAttachment(companyId, deleting.id, controller.signal);
      if (controller.signal.aborted) return;
      setDeleting(null);
      /*
       * ⚠️ **A re-read, not a local filter.** Delete answers no body, so there is
       * no list to render; splicing the row out here would be a second source of
       * truth for what this company has attached, and it would already be wrong
       * if a colleague had attached something while the dialog was open.
       */
      reload();
    } catch (caught) {
      if (controller.signal.aborted) return;
      setDeleteError(messageOf(caught, 'The attachment couldn’t be deleted.'));
    } finally {
      setDeletePending(false);
      if (deleteAbortRef.current === controller) deleteAbortRef.current = null;
    }
  }, [companyId, deletePending, deleting, reload]);

  /** Step 3 alone, for bytes that reached storage before the dialog was closed. */
  const recordPending = useCallback(async () => {
    if (unrecorded === null || recovering) return;

    const controller = new AbortController();
    recoverAbortRef.current?.abort();
    recoverAbortRef.current = controller;

    setRecovering(true);
    setRecoverError(null);

    try {
      const answer = await api.createCompanyAttachment(
        companyId,
        {
          kind: 'file',
          storageKey: unrecorded.storageKey,
          fileName: unrecorded.fileName,
          title: unrecorded.title,
          ...(unrecorded.note === '' ? {} : { note: unrecorded.note }),
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setUnrecorded(null);
      replaceList(answer.data);
    } catch (caught) {
      if (controller.signal.aborted) return;
      /*
       * ⚠️ The notice above is untouched by a failure here: the fact that
       * matters — the bytes are up and unrecorded — has not changed, so it must
       * not be replaced by a sentence about this attempt. The refusal is added
       * BESIDE it, because a control that appears to do nothing is the worse
       * failure of the two.
       */
      setRecoverError(messageOf(caught, 'It still couldn’t be recorded.'));
    } finally {
      setRecovering(false);
      if (recoverAbortRef.current === controller) recoverAbortRef.current = null;
    }
  }, [companyId, recovering, replaceList, unrecorded]);

  /*
   * Derived during render rather than set at the top of the effect: there is no
   * window in which a retry looks settled, and a change of company cannot leave
   * the previous account's attachments on screen reading as this one's.
   */
  const state: ListState = result?.key === key ? result.state : { status: 'loading' };
  const list = state.status === 'ready' ? state.list : [];
  const error = state.status === 'error' ? state.message : null;

  /*
   * ⚠️ **Withheld rather than disabled for a reader who may not write.**
   * A disabled control needs a sentence saying why, and a permanent
   * why-you-cannot line in the header of a panel on every company
   * record is exactly the helper text the house rule forbids — with no
   * control for it to explain, because that reader was never going to
   * press it. One element, mounted in the header or in the embedded
   * toolbar, so the two modes cannot gate it differently.
   */
  const attachControl = canUpdate ? (
    <Button variant="secondary" size="sm" onClick={() => setAttachOpen(true)}>
      Attach
    </Button>
  ) : undefined;

  const content = (
    <>
      {embedded && attachControl !== undefined && (
        <div className="flex justify-end gap-2 px-4 pt-3">{attachControl}</div>
      )}

        {/*
          ⚠️ THE THREE STATES ARE MUTUALLY EXCLUSIVE, AND THE ERROR ARM RENDERS
          INSTEAD OF THE EMPTY ONE RATHER THAN ABOVE IT. A read that failed and a
          company with nothing attached are different facts, and nothing attached
          is the ordinary answer here — so an empty state under a red banner is
          the one arrangement a reader could take for a settled result. The retry
          below writes nothing; it re-reads.
        */}
        <div aria-busy={state.status === 'loading' || undefined}>
          {state.status === 'loading' ? (
            <AttachmentsSkeleton />
          ) : error !== null ? (
            <div
              role="alert"
              className="flex flex-col items-start gap-2 px-4 py-4 text-xs text-danger"
            >
              <span className="font-semibold">Couldn’t load attachments.</span>
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
              <Button size="sm" onClick={reload}>
                Try again
              </Button>
            </div>
          ) : list.length === 0 ? (
            <EmptyState
              icon={<PaperclipIcon />}
              title="Nothing attached yet"
              /*
                ⚠️ **The description is the NEXT ACTION and nothing else**, and it
                is withheld from a reader who cannot take it: pointing somebody at
                a control they are not being shown is worse than saying nothing.
                ⛔ No "files will appear here" — empty-state copy may never imply
                data is on its way.
              */
              description={canUpdate ? 'Use Attach to add a file or a link.' : undefined}
            />
          ) : (
            <div>
              {list.map((attachment) => (
                <AttachmentRow
                  key={attachment.id}
                  companyId={companyId}
                  attachment={attachment}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  onEdit={setEditing}
                  onDelete={(target) => {
                    setDeleteError(null);
                    setDeleting(target);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/*
          🔴 **BYTES IN STORAGE WITH NOTHING POINTING AT THEM.** The list above
          cannot show this — that is the entire problem — so the panel says it,
          and goes on saying it until the record is made or the reader stops. It
          renders only when there is something to say, which is the same rule the
          footer on the event-interest panel follows.
        */}
        {unrecorded !== null && (
          <PanelFooter>
            <div className="min-w-0">
              <p role="status" className="m-0 text-xs text-warning [overflow-wrap:anywhere]">
                {unrecorded.fileName} finished uploading and was not recorded, so it is not in the
                list above.
              </p>
              {recoverError !== null && (
                <p role="alert" className="m-0 mt-1 text-xs text-danger [overflow-wrap:anywhere]">
                  {recoverError}
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button size="sm" loading={recovering} onClick={() => void recordPending()}>
                Record it
              </Button>
              {/*
                ⚠️ **The label says what it does and does not overclaim.** Nothing
                here can reach the uploaded bytes — there is no endpoint for an
                unrecorded object — so a reader dismissing this is deciding to
                stop trying, not deciding to clean anything up, and a word like
                "Discard" would say the opposite.
              */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUnrecorded(null);
                  setRecoverError(null);
                }}
              >
                Stop trying
              </Button>
            </div>
          </PanelFooter>
        )}
    </>
  );

  return (
    <>
      {embedded ? (
        content
      ) : (
        <Panel aria-labelledby={HEADING_ID}>
          <PanelHeader id={HEADING_ID} title="Attachments" actions={attachControl} />
          {content}
        </Panel>
      )}

      {/*
        ⚠️ **Mounted CONDITIONALLY, outside the `Panel`.** Conditionally, so each
        dialog's state is initialised afresh from the record it was opened for
        rather than surviving from the last one; outside, so the markup stays
        flat and nothing here depends on `Modal` portalling to the body, which it
        does anyway.
      */}
      {attachOpen && (
        <AttachDialog
          companyId={companyId}
          onClose={() => setAttachOpen(false)}
          onAttached={replaceList}
          onLeftUnrecorded={setUnrecorded}
        />
      )}

      {editing !== null && (
        <EditDialog
          /*
           * Keyed on the row as well as mounted conditionally: a reader opening
           * a second attachment's editor without an intervening unmounted render
           * would otherwise see the first one's title, because the form's state
           * is seeded from props at mount.
           */
          key={editing.id}
          companyId={companyId}
          attachment={editing}
          onClose={() => setEditing(null)}
          onSaved={replaceList}
        />
      )}

      {deleting !== null && (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(null);
            setDeleteError(null);
          }}
          onConfirm={() => void confirmDelete()}
          title={`Delete ${deleting.title}?`}
          description={deleteDescription(deleting)}
          confirmLabel={deleting.kind === 'file' ? 'Delete file' : 'Delete link'}
          loading={deletePending}
          error={deleteError}
        />
      )}
    </>
  );
}
